import { useEffect, useRef, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { usePwaInstall, openInAndroidBrowser } from "./usePwaInstall";
import { Button, CloseIcon } from "@a2z/ui";

// ---------------------------------------------------------------------------
// Throttle logic — max 2 shows per week, with at least 2 days between them
// ---------------------------------------------------------------------------

const MODAL_SHOW_KEY = "seller_pwa_modal_shown_at_v1";
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;

function getRecentTimestamps(): number[] {
  try {
    const raw = window.localStorage.getItem(MODAL_SHOW_KEY);
    if (!raw) return [];
    const timestamps = JSON.parse(raw) as number[];
    const cutoff = Date.now() - ONE_WEEK_MS;
    return timestamps.filter((t) => t > cutoff);
  } catch {
    return [];
  }
}

function canShowModal(): boolean {
  const recent = getRecentTimestamps();
  if (recent.length >= 2) return false;
  if (recent.length === 1) {
    const lastShown = Math.max(...recent);
    return Date.now() - lastShown >= TWO_DAYS_MS;
  }
  return true;
}

function recordModalShow(): void {
  try {
    const recent = getRecentTimestamps();
    recent.push(Date.now());
    window.localStorage.setItem(MODAL_SHOW_KEY, JSON.stringify(recent));
  } catch {
    // localStorage unavailable (private mode / quota) — skip persistence.
  }
}

// ---------------------------------------------------------------------------
// Helper — open current URL in Safari on iOS
// ---------------------------------------------------------------------------

function openInSafari(): void {
  const url = window.location.href;
  // x-safari-https:// forces iOS to open the URL in Safari
  window.location.href = url.replace(/^https?:\/\//, (scheme) =>
    scheme === "https://" ? "x-safari-https://" : "x-safari-http://",
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InstallPrompt() {
  const t = useTranslations();
  const {
    canInstall,
    isInstalled,
    isIosSafari,
    isIosNonSafari,
    isAndroidManual,
    isInAppBrowser,
    isReady,
    install,
  } = usePwaInstall();
  // Engagement + weekly-throttle gate, sampled once per mount (lazy
  // initializer — the localStorage reads must not run during re-renders).
  const [autoOpenGateOk] = useState(
    () =>
      typeof window !== "undefined" &&
      localStorage.getItem("seller_pwa_engagement_v1") === "true" &&
      canShowModal(),
  );
  const [closedByUser, setClosedByUser] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);

  // Auto-open when conditions are met (mobile-only via CSS on the wrapper).
  // Derived during render instead of set from an effect; the render guards
  // below hide the whole subtree under the same eligibility conditions.
  const eligible =
    isReady &&
    !isInstalled &&
    (canInstall || isIosSafari || isIosNonSafari || isAndroidManual || isInAppBrowser);
  const isModalOpen = eligible && autoOpenGateOk && !closedByUser;

  // Persist the throttle timestamp once the modal actually shows.
  const recordedShowRef = useRef(false);
  useEffect(() => {
    if (isModalOpen && !recordedShowRef.current) {
      recordedShowRef.current = true;
      recordModalShow();
    }
  }, [isModalOpen]);

  const handleClose = () => {
    setClosedByUser(true);
    setShowFollowUp(true);
  };

  const handleInstall = async () => {
    await install();
    setClosedByUser(true);
  };

  if (!isReady || isInstalled) return null;
  if (!canInstall && !isIosSafari && !isIosNonSafari && !isAndroidManual && !isInAppBrowser)
    return null;

  const bodyText = isInAppBrowser
    ? t(keys.seller.pwaInAppBrowserBody)
    : isAndroidManual
      ? t(keys.seller.pwaAndroidManualBody)
      : isIosSafari
        ? t(keys.seller.pwaModalIosBody)
        : isIosNonSafari
          ? t(keys.seller.pwaModalIosNonSafariBody)
          : t(keys.seller.pwaModalBody);

  return (
    // Mobile-only wrapper — the modal uses fixed positioning so this just
    // acts as a guard; on desktop the whole subtree is hidden.
    <div className="lg:hidden">
      {/* Install modal */}
      {isModalOpen ? (
        <div
          className="fixed inset-x-0 top-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center lg:inset-0"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleClose();
          }}
        >
          <div className="w-full max-w-sm rounded-2xl bg-[color:var(--surface)] p-6 shadow-2xl">
            {/* Header */}
            <div className="mb-3 flex items-start justify-between gap-3">
              <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">
                {t(keys.seller.pwaModalTitle)}
              </h2>
              <Button
                variant="link"
                size="sm"
                onClick={handleClose}
                aria-label={t(keys.seller.pwaInstallDismissAction)}
                leadingIcon={<CloseIcon size={18} />}
              />

            </div>

            {/* Body */}
            <p className="mb-6 text-sm leading-relaxed text-[color:var(--text-secondary)]">
              {bodyText}
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="lg"
                onClick={handleClose}
                className="flex-1"
              >
                {t(keys.seller.pwaInstallDismissAction)}
              </Button>
              {canInstall ? (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => void handleInstall()}
                  className="flex-1"
                >
                  {t(keys.seller.pwaInstallAction)}
                </Button>
              ) : isIosNonSafari ? (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={openInSafari}
                  className="flex-1"
                >
                  {t(keys.seller.pwaOpenInSafariAction)}
                </Button>
              ) : isInAppBrowser ? (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={openInAndroidBrowser}
                  className="flex-1"
                >
                  {t(keys.seller.pwaOpenInBrowserAction)}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {/* Follow-up hint shown after modal is closed */}
      {showFollowUp && !isModalOpen ? (
        <div className="mb-4 rounded-xl bg-[color:var(--background-muted)] px-4 py-3 text-sm text-[color:var(--text-muted)]">
          {t(keys.seller.pwaModalFollowUp)}
        </div>
      ) : null}
    </div>
  );
}
